<template>
  <div class="transfer-samples-dialog-container">
    <div class="transfer-samples-table-container">
      <DxDataGrid
        ref="transferSamplesTable"
        :dataSource="tableData"
        height="100%"
        :showBorders="true"
        :rowAlternationEnabled="true"
        @toolbarPreparing="onToolbarPreparing"
        @selectionChanged="onSelectionChanged"
      >
        <DxSearchPanel :visible="true" :width="240" />
        <DxHeaderFilter :visible="true" />
        <DxSelection mode="single" selectAllMode="all-pages" />
        <DxPaging :enabled="false" />
        <DxLoadPanel :enabled="true" />
        <DxScrolling mode="standard" rowRenderingMode="standard" />
        <DxColumn dataField="MpName" :caption="$t('project.MeasurementPointName')" />
        <DxColumn dataField="FtName" :caption="$t('project.GaugingTubeName')" />
        <DxColumn dataField="FtFilterFrom" :caption="$t('project.GaugingTubeFrom')" />
        <DxColumn dataField="FtFilterTo" :caption="$t('project.GaugingTubeTo')" />
        <template #tableTitleTemplate>
          <div class="transfer-samples-dialog-header-text">
            {{ $t('transferSamplesDialog.header') }}
          </div>
        </template>
      </DxDataGrid>
    </div>
    <NvLoading :show="isTransferringSamples"></NvLoading>
  </div>
</template>

<script>
import DxDataGrid, { DxColumn, DxHeaderFilter, DxPaging, DxScrolling, DxSelection, DxLoadPanel, DxSearchPanel } from 'devextreme-vue/data-grid'

import sortUtils from '@/utils/sortUtils'

import NvLoading from '@/components/_shared/loading'

export default {
  name: 'TransferSamplesDialog',
  components: { DxDataGrid, DxColumn, DxHeaderFilter, DxSelection, DxScrolling, DxPaging, DxLoadPanel, DxSearchPanel, NvLoading },
  emits: ['samplesTransferred', 'selectionChanged'],
  data() {
    return {
      measurementPoints: [],
      gaugingTubes: [],
      /**
       * @type Array<{ measurementPoint: Object, gaugingTube: Object | undefined }>
       */
      gaugingTubesInMeasurementPoints: [],
      isLoading: true,
      tableData: [],
      selectedRow: undefined,
      isTransferringSamples: false
    }
  },
  props: {
    projectId: {
      type: String,
      required: true
    },
    selectedSamples: {
      type: Array,
      required: true
    },
    dialogOpened: {
      type: Boolean,
      required: true
    },
    sampleType: {
      type: String,
      required: true
    }
  },
  watch: {
    dialogOpened(value) {
      if (value) {
        this.onDialogOpened()
      } else {
        this.onDialogClosed()
      }
    }
  },
  computed: {
    sortedData() {
      return this.gaugingTubesInMeasurementPoints.map((tuple) => {
        return {
          MpGuid: tuple.measurementPoint.MpGuid,
          FtGuid: tuple.gaugingTube ? tuple.gaugingTube.FtGuid : '',
          MpName: tuple.measurementPoint.MpName,
          FtName: tuple.gaugingTube ? tuple.gaugingTube.FtName : '',
          FtFilterFrom: tuple.gaugingTube ? tuple.gaugingTube.FtFilterFrom : '',
          FtFilterTo: tuple.gaugingTube ? tuple.gaugingTube.FtFilterTo : ''
        }
      }).sort((left, right) => sortUtils.naturalSort(left.MpName + '-' + left.FtName, right.MpName + '-' + right.FtName))
    },
    transferSamplesTableInstance() {
      return this.$refs.transferSamplesTable.instance
    }
  },
  methods: {
    onToolbarPreparing(event) {
      event.toolbarOptions.items.push({
        location: 'before',
        template: 'tableTitleTemplate',
        sortIndex: 0
      })
    },
    async onDialogOpened() {
      this.isLoading = true
      this.transferSamplesTableInstance.beginCustomLoading(this.$t('other.loading'))
      await this.loadMeasurementPoints()
      this.isLoading = false
      this.transferSamplesTableInstance.endCustomLoading()
    },
    async onDialogClosed() {
      this.clearData()
    },
    clearData() {
      this.measurementPoints = []
      this.gaugingTubes = []
      this.gaugingTubesInMeasurementPoints = []
      this.tableData = []
    },
    async loadMeasurementPoints() {
      const measurementPoints = await this.$store.dispatch('getMeasurementPoints', {
        projectId: this.projectId
      })

      // Resolving these awaits sequentially.
      // If we want to resolve these in parallel the user might wait for a long
      // time for the resolving of data, while not allowing them to perform any
      // other actions. Currently we allow the user to close the popup halfway
      // so users don't have to stay inside the window.
      for (const measurementPoint of measurementPoints) {
        this.measurementPoints.push(measurementPoint)

        await this.loadGaugingTubes(measurementPoint)

        // Add extra entry without gauging tube to transfer sample to measurement point instead
        this.gaugingTubesInMeasurementPoints.push({ measurementPoint })
        if (!this.dialogOpened) {
          this.gaugingTubesInMeasurementPoints = []
          return
        }
      }
      this.tableData = this.sortedData
    },
    async loadGaugingTubes(measurementPoint) {
      const gaugingTubes = await this.$store.dispatch('getGaugingTubes', {
        projectId: this.projectId,
        measurementPointGuid: measurementPoint.MpGuid
      })

      for (const gaugingTube of gaugingTubes) {
        this.gaugingTubes.push(gaugingTube)
        this.gaugingTubesInMeasurementPoints.push({ measurementPoint, gaugingTube })
        if (!this.dialogOpened) return
      }
    },
    onSelectionChanged(data) {
      this.selectedRow = data.selectedRowsData[0]
      this.emitSelectionChanged()
    },
    emitSelectionChanged() {
      this.$emit('selectionChanged', this.selectedRow)
    },
    clearSelection() {
      if (!this.transferSamplesTableInstance) return
      this.transferSamplesTableInstance.clearSelection()
    },
    async onFooterButtonClicked() {
      await this.transferSamples()
    },
    async transferSamples() {
      this.isTransferringSamples = true
      this.$emit('transferringSamples')
      const selectedMpGuid = this.selectedRow.MpGuid
      const selectedFtGuid = this.selectedRow.FtGuid

      const { measurementPoint, gaugingTube } = this.getSelectedRowData(selectedMpGuid, selectedFtGuid)

      await this.$store.dispatch('transferWaterSample', {
        waterSamples: this.selectedSamples,
        projectId: this.projectId,
        measurementPoint,
        gaugingTube
      })

      this.isTransferringSamples = false
      this.$emit('samplesTransferred')
    },
    getSelectedRowData(MpGuid, FtGuid) {
      const selectedMeasurementPoint = this.measurementPoints.find((measurementPoint) => measurementPoint.MpGuid === MpGuid)

      let selectedGaugingTube
      if (FtGuid && FtGuid !== '') {
        selectedGaugingTube = this.gaugingTubes.find((gaugingTube) => gaugingTube.FtGuid === FtGuid)
      }

      return { measurementPoint: selectedMeasurementPoint, gaugingTube: selectedGaugingTube }
    }
  }
}
</script>

<!-- need to override dev extreme styles so can't be scoped -->
<style lang="less">
.transfer-samples-dialog-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px;
  gap: 16px;
  .transfer-samples-table-container {
    .dx-gridbase-container > .dx-datagrid-rowsview.dx-scrollable > .dx-scrollable-wrapper {
      max-height: 512px;
    }
  }
  .confirmation-box {
    height: 128px;
    width: 512px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    .warning-icon {
      color: @warning500;
      font-size: 68px;
    }
    .spinner-icon {
      color: @gray700;
      font-size: 68px;
      animation: gif 1.5s infinite linear;
      @keyframes gif {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    }
    .confirmation-text {
      text-align: center;
      font-size: 16px;
      font-weight: bold;
      color: @gray700;
    }
  }
}
.transfer-samples-dialog-container .dx-toolbar-before {
  font-weight: 500;
  flex-grow: 1;
  color: @gray700;
  font-size: 18px;
}
.transfer-samples-dialog-container .dx-datagrid-header-panel {
  background-color: transparent;
  .dx-toolbar {
    background-color: transparent;
  }
}
</style>
